import React from 'react';
import { motion } from 'framer-motion';
import {
  Ruler,
  Compass,
  Circle,
  Trash2,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';

// Calculate distance between two 3D points
const calculateDistance = (p1, p2) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dz = p2.z - p1.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

// Calculate angle between three points (p2 is vertex)
const calculateAngle = (p1, p2, p3) => {
  const v1 = { x: p1.x - p2.x, y: p1.y - p2.y, z: p1.z - p2.z };
  const v2 = { x: p3.x - p2.x, y: p3.y - p2.y, z: p3.z - p2.z };
  
  const dot = v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
  const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y + v1.z * v1.z);
  const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y + v2.z * v2.z);
  
  const cosAngle = dot / (mag1 * mag2);
  return Math.acos(Math.max(-1, Math.min(1, cosAngle))) * (180 / Math.PI);
};

export const MeasurementPanel = ({
  isActive,
  measurementType,
  onMeasurementTypeChange,
  selectedPoints,
  measurements,
  onClearPoints,
  onDeleteMeasurement,
  unit,
  onUnitChange
}) => {
  const currentMeasurement = selectedPoints.length >= 2 ? {
    type: measurementType,
    value: measurementType === 'angular' && selectedPoints.length >= 3
      ? calculateAngle(selectedPoints[0], selectedPoints[1], selectedPoints[2])
      : calculateDistance(selectedPoints[0], selectedPoints[1])
  } : null;
  
  const formatValue = (value, type) => {
    if (type === 'angular') {
      return `${value.toFixed(1)}°`;
    }
    // Scale factor to make measurements more realistic (assuming 1 unit = 10mm)
    const scaledValue = value * 10;
    if (unit === 'inch') {
      return `${(scaledValue / 25.4).toFixed(3)} in`;
    }
    return `${scaledValue.toFixed(2)} mm`;
  };
  
  return (
    <motion.div
      className="absolute top-20 left-4 w-72 bg-card/90 backdrop-blur border border-border rounded-lg shadow-lg"
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: isActive ? 0 : -300, opacity: isActive ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      data-testid="measurement-panel"
    >
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Ruler className="w-4 h-4 text-primary" />
          Measurement Tools
        </h3>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Measurement type selector */}
        <div className="flex gap-2">
          <Button
            variant={measurementType === 'linear' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onMeasurementTypeChange('linear')}
            className="flex-1"
            data-testid="measure-linear-btn"
          >
            <Ruler className="w-4 h-4 mr-1" />
            Linear
          </Button>
          <Button
            variant={measurementType === 'angular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onMeasurementTypeChange('angular')}
            className="flex-1"
            data-testid="measure-angular-btn"
          >
            <Compass className="w-4 h-4 mr-1" />
            Angular
          </Button>
          <Button
            variant={measurementType === 'radial' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onMeasurementTypeChange('radial')}
            className="flex-1"
            data-testid="measure-radial-btn"
          >
            <Circle className="w-4 h-4 mr-1" />
            Radial
          </Button>
        </div>
        
        {/* Unit toggle */}
        <div className="flex items-center justify-between">
          <Label className="text-xs">Unit</Label>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${unit === 'mm' ? 'text-primary' : 'text-muted-foreground'}`}>mm</span>
            <Switch
              checked={unit === 'inch'}
              onCheckedChange={(checked) => onUnitChange(checked ? 'inch' : 'mm')}
              data-testid="unit-toggle"
            />
            <span className={`text-xs ${unit === 'inch' ? 'text-primary' : 'text-muted-foreground'}`}>inch</span>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded">
          {measurementType === 'linear' && 'Click two points on the model to measure distance'}
          {measurementType === 'angular' && 'Click three points to measure angle (2nd point is vertex)'}
          {measurementType === 'radial' && 'Click center and edge point to measure radius'}
        </div>
        
        {/* Selected points */}
        {selectedPoints.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Selected Points</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearPoints}
                className="h-6 px-2"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            {selectedPoints.map((point, i) => (
              <div key={i} className="text-xs font-mono bg-secondary/50 rounded px-2 py-1">
                P{i + 1}: ({point.x.toFixed(2)}, {point.y.toFixed(2)}, {point.z.toFixed(2)})
              </div>
            ))}
          </div>
        )}
        
        {/* Current measurement */}
        {currentMeasurement && (
          <div className="p-3 bg-primary/10 border border-primary/30 rounded">
            <div className="text-xs text-muted-foreground">Current Measurement</div>
            <div className="text-lg font-mono font-bold text-primary">
              {formatValue(currentMeasurement.value, currentMeasurement.type)}
            </div>
          </div>
        )}
        
        {/* Saved measurements */}
        {measurements.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium">Saved Measurements</div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {measurements.map((m, i) => (
                <div key={m.id || i} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-xs">
                  <span className="font-mono">{formatValue(m.value, m.measurement_type)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteMeasurement(m.id)}
                    className="h-6 w-6 p-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MeasurementPanel;
